<template>
    <div class="payment-split-settings">
        <div class="d-flex align-items-center justify-content-between mb-3">
            <label class="font-weight-bold mb-0">
                {{ __t('SPLIT_RULES') }}
            </label>

            <b-button size="sm" variant="primary" @click="addSplitRow">
                <i class="fal fa-plus mr-1"></i>
                {{ __t('ADD') }}
            </b-button>
        </div>

        <div v-if="splitRows.length <= 0" class="alert alert-info">
            Split rules ще не додані
        </div>

        <b-tabs v-if="splitRows.length > 0" pills card>
            <b-tab
                v-for="(row, rowIndex) in splitRows"
                :key="row._key || rowIndex"
                :active="rowIndex === 0"
            >
                <template #title>
                    <div class="px-3">
                        {{ getTabTitle(row, rowIndex) }}
                    </div>
                </template>

                <div class="split-card">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                        <strong>
                            Split #{{ rowIndex + 1 }}
                        </strong>

                        <b-button
                            size="sm"
                            variant="danger"
                            @click="removeSplitRow(rowIndex)"
                        >
                            {{ __t('REMOVE') }}
                        </b-button>
                    </div>

                    <div class="row mb-3">
                        <div
                            v-for="fieldKey in rowFieldKeys(row)"
                            :key="fieldKey"
                            class="col-12"
                        >
                            <b-form-group :label="fieldKey">
                                <v-select
                                    v-if="fieldKey === 'splitReceiverId'"
                                    :value="getSplitterOption(row[fieldKey])"
                                    :options="splitters"
                                    label="label"
                                    :clearable="true"
                                    :searchable="true"
                                    :disabled="isLoadSplitters"
                                    @input="setSplitterValue(row, fieldKey, $event)"
                                >
                                    <template #option="option">
                                        <div>
                                            <strong>{{ option.name }}</strong>
                                            <div
                                                class="small text-muted"
                                                v-if="option.okpo || option.edrpou"
                                            >
                                                {{ option.okpo || option.edrpou }}
                                            </div>
                                        </div>
                                    </template>

                                    <template #selected-option="option">
                                        <span>
                                            {{ option.name }}
                                            <small v-if="option.okpo || option.edrpou">
                                                / {{ option.okpo || option.edrpou }}
                                            </small>
                                        </span>
                                    </template>
                                </v-select>

                                <b-form-input
                                    v-else
                                    v-model="row[fieldKey]"
                                    :placeholder="fieldKey"
                                    @input="emitChange"
                                />
                            </b-form-group>
                        </div>
                    </div>

                    <div class="split-orgs">
                        <div class="d-flex align-items-center justify-content-between mb-3">
                            <div>
                                <label class="font-weight-bold mb-0">
                                    {{ __t('CATEGORIES') }}
                                </label>

                                <div class="text-muted small">
                                    Виберіть організацію і категорії для цього split
                                </div>
                            </div>

                            <div class="text-muted small">
                                Всього вибрано: {{ getTotalSelectedForRow(row) }}
                            </div>
                        </div>

                        <div v-if="isLoadOrganizations" class="p-3">
                            <i class="fal fa-spinner fa-spin"></i>
                            Завантаження організацій...
                        </div>

                        <div v-else-if="drawOrganizations.length <= 0" class="alert alert-info">
                            Немає організацій з товарами
                        </div>

                        <div v-else>
                            <b-form-group label="Організація">
                                <v-select
                                    :value="getSelectedOrg(row)"
                                    :options="drawOrganizations"
                                    label="org_label"
                                    :clearable="false"
                                    :searchable="true"
                                    placeholder="Виберіть організацію..."
                                    @input="setSelectedOrg(row, $event)"
                                >
                                    <template #option="org">
                                        <div>
                                            <strong>{{ getOrgTitle(org) }}</strong>
                                            <div class="small text-muted">
                                                ID: {{ org.id }} / Товарів: {{ org.attached_products_clean_count }}
                                            </div>
                                        </div>
                                    </template>

                                    <template #selected-option="org">
                                        <span>
                                            {{ getOrgTitle(org) }}
                                            <small class="text-muted">
                                                / {{ org.attached_products_clean_count }}
                                            </small>
                                        </span>
                                    </template>
                                </v-select>
                            </b-form-group>

                            <div v-if="!getSelectedOrg(row)" class="alert alert-light border">
                                Виберіть організацію, щоб показати категорії
                            </div>

                            <div v-else class="org-active-card">
                                <div class="org-active-head">
                                    <div>
                                        <div class="org-active-title">
                                            {{ getOrgTitle(getSelectedOrg(row)) }}
                                        </div>

                                        <div class="org-active-meta">
                                            ID: {{ getSelectedOrg(row).id }} /
                                            Вибрано: {{ getTotalSelectedForRow(row) }}
                                        </div>
                                    </div>

                                    <div>
                                        <b-button
                                            size="sm"
                                            variant="outline-secondary"
                                            class="mr-2"
                                            @click="clearOrgCategories(row)"
                                        >
                                            {{ __t('CLEAR') }}
                                        </b-button>

                                        <b-button
                                            size="sm"
                                            variant="outline-primary"
                                            @click="loadCategories(getSelectedOrg(row), true)"
                                        >
                                            <i class="fal fa-sync"></i>
                                        </b-button>
                                    </div>
                                </div>

                                <div v-if="isCategoryLoading(getSelectedOrg(row).id)" class="p-3">
                                    <i class="fal fa-spinner fa-spin"></i>
                                    Завантаження категорій...
                                </div>

                                <div v-else class="categories-scroll">
                                    <categlist
                                        @change="setActiveCategory($event, row, rowIndex, getSelectedOrg(row))"
                                        :categories="getOrgCategoriesForRow(row, getSelectedOrg(row))"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </b-tab>
        </b-tabs>
    </div>
</template>

<script>
import vSelect from "vue-select";
import Categlist from "../../../../catalog/categories/list/categlist";

export default {
    name: "split_tab",

    components: {
        vSelect,
        Categlist
    },

    props: {
        payment: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            splitters: [],
            isLoadSplitters: false,

            organizations: [],
            isLoadOrganizations: false,

            categoriesByOrg: {},
            loadingByOrg: {}
        }
    },

    created() {
        this.normalizeSplit();
        this.loadSplitters();
        this.loadOrganizations();
    },

    methods: {
        normalizeSplit() {
            if (!this.payment.split || typeof this.payment.split !== 'object') {
                this.$set(this.payment, 'split', {
                    split_payment_data: [],
                    split_template: [],
                    split_categories: []
                });
            }

            if (!Array.isArray(this.payment.split.split_payment_data)) {
                this.$set(this.payment.split, 'split_payment_data', []);
            }

            if (!Array.isArray(this.payment.split.split_template)) {
                this.$set(this.payment.split, 'split_template', []);
            }

            if (!Array.isArray(this.payment.split.split_categories)) {
                this.$set(this.payment.split, 'split_categories', []);
            }

            this.payment.split.split_payment_data = this.payment.split.split_payment_data.map((item) => {
                return this.normalizeSplitRow(item);
            });

            this.rebuildSplitCategories();
        },

        normalizeSplitRow(item) {
            if (!item || typeof item !== 'object') {
                item = {};
            }

            let categs = item.categs || item.split_categories || [];

            if (!Array.isArray(categs) && item.org_categs && typeof item.org_categs === 'object') {
                categs = [];

                Object.values(item.org_categs).forEach((ids) => {
                    if (!Array.isArray(ids)) {
                        return;
                    }

                    ids.forEach((id) => {
                        categs.push(id);
                    });
                });
            }

            if (typeof categs === 'string') {
                try {
                    categs = JSON.parse(categs);
                } catch (e) {
                    categs = [];
                }
            }

            if (!Array.isArray(categs)) {
                categs = [];
            }

            return {
                ...item,
                _key: item._key || item.id || this.makeKey(),
                active_org_id: parseInt(item.active_org_id || item.organization_id || 0),
                categs: categs
                    .map((id) => parseInt(id))
                    .filter((id) => id > 0)
            };
        },

        async loadSplitters() {
            if (!this.hasSplitReceiverField()) {
                return;
            }

            const gateId = parseInt(this.payment.pay_gate_id || 0);

            if (gateId <= 0) {
                return;
            }

            this.isLoadSplitters = true;

            await api.get('/internal/v1/payment/gates/' + gateId + '/by_settings/' + this.payment.id + '/splitters')
                .then(({data}) => {
                    this.isLoadSplitters = false;

                    if (data.status === 'operation_success') {
                        this.splitters = this.normalizeSplitters(data.data);
                    }
                })
                .catch(() => {
                    this.isLoadSplitters = false;
                });
        },

        normalizeSplitters(items) {
            if (!Array.isArray(items)) {
                return [];
            }

            return items.map((item) => {
                const id = item.id || '';
                const name = item.name || id;
                const okpo = item.okpo || item.edrpou || '';

                return {
                    id: id,
                    name: name,
                    okpo: item.okpo || '',
                    edrpou: item.edrpou || '',
                    label: okpo ? name + ' / ' + okpo : name
                };
            }).filter((item) => item.id !== '');
        },

        async loadOrganizations() {
            this.organizations = [];
            this.isLoadOrganizations = true;

            await api.post('/internal/v1/catalog/organizations/with/products_count', {
                filter: {
                    has_rest_provider: 'pwa'
                }
            })
                .then(({data}) => {
                    this.isLoadOrganizations = false;

                    if (data.status === 'operation_success') {
                        this.organizations = Array.isArray(data.data)
                            ? data.data.map((org) => {
                                return {
                                    ...org,
                                    org_label: this.getOrgTitle(org) + ' / ' + (org.attached_products_clean_count || 0)
                                };
                            })
                            : [];

                        this.setDefaultOrganizations();
                    }
                })
                .catch(({response}) => {
                    this.isLoadOrganizations = false;

                    EventBus.$emit('show_toast', {
                        err: response?.data?.message || 'Organizations load error',
                        type: "danger",
                        title: response?.data?.status || 'Payment Split'
                    });
                });
        },

        setDefaultOrganizations() {
            if (this.drawOrganizations.length <= 0) {
                return;
            }

            this.splitRows.forEach((row) => {
                if (!row.active_org_id) {
                    this.$set(row, 'active_org_id', parseInt(this.drawOrganizations[0].id));
                }

                const org = this.getSelectedOrg(row);

                if (org) {
                    this.loadCategories(org);
                }
            });

            this.emitChange();
        },

        async loadCategories(org, force = false) {
            if (!org) {
                return;
            }

            const orgId = parseInt(org.id || 0);

            if (orgId <= 0) {
                return;
            }

            if (!force && this.categoriesByOrg.hasOwnProperty(orgId)) {
                return;
            }

            this.$set(this.loadingByOrg, orgId, true);

            await api.post('/internal/v1/catalog/categories/tree', {
                org_id: orgId,
                provider_filter: 'pwa'
            }).then(({data}) => {
                this.$set(this.loadingByOrg, orgId, false);

                if (data.status === 'operation_success') {
                    this.$set(
                        this.categoriesByOrg,
                        orgId,
                        Array.isArray(data.data) ? data.data : []
                    );
                }
            }).catch(({response}) => {
                this.$set(this.loadingByOrg, orgId, false);

                EventBus.$emit('show_toast', {
                    err: response?.data?.message || 'Categories load error',
                    type: "danger",
                    title: response?.data?.status || 'Payment Split'
                });
            });
        },

        addSplitRow() {
            const row = this.normalizeSplitRow(this.getDefaultRow());

            if (!row.active_org_id && this.drawOrganizations.length > 0) {
                row.active_org_id = parseInt(this.drawOrganizations[0].id);
            }

            this.splitRows.push(row);

            const org = this.getSelectedOrg(row);

            if (org) {
                this.loadCategories(org);
            }

            this.rebuildSplitCategories();
            this.emitChange();
        },

        removeSplitRow(rowIndex) {
            this.splitRows.splice(rowIndex, 1);

            this.rebuildSplitCategories();
            this.emitChange();
        },

        getDefaultRow() {
            const template = this.payment?.split?.split_template || [];

            if (Array.isArray(template) && template.length > 0) {
                let row = JSON.parse(JSON.stringify(template[0]));

                row.categs = [];
                row.active_org_id = 0;

                delete row.org_categs;
                delete row.organization_id;
                delete row.split_categories;

                return row;
            }

            const existing = this.splitRows.find((row) => {
                return row && typeof row === 'object';
            });

            if (existing) {
                let row = {};

                Object.keys(existing).forEach((key) => {
                    if (this.isSystemField(key)) {
                        return;
                    }

                    row[key] = '';
                });

                row.categs = [];
                row.active_org_id = 0;

                return row;
            }

            return {
                categs: [],
                active_org_id: 0
            };
        },

        rowFieldKeys(row) {
            return Object.keys(row).filter((key) => {
                return !this.isSystemField(key);
            });
        },

        isSystemField(key) {
            return [
                'id',
                '_key',
                'categories',
                'org_categs',
                'categs',
                'active_org_id',
                'organization_id',
                'split_categories'
            ].includes(key);
        },

        setSelectedOrg(row, org) {
            const orgId = org ? parseInt(org.id || 0) : 0;
            const oldOrgId = parseInt(row.active_org_id || 0);

            this.$set(row, 'active_org_id', orgId);

            if (oldOrgId !== orgId) {
                this.$set(row, 'categs', []);
            }

            if (orgId > 0) {
                this.loadCategories(org);
            }

            this.rebuildSplitCategories();
            this.emitChange();
        },

        getSelectedOrg(row) {
            if (!row || !row.active_org_id) {
                return null;
            }

            return this.drawOrganizations.find((org) => {
                return parseInt(org.id) === parseInt(row.active_org_id);
            }) || null;
        },

        setActiveCategory(categ, row, rowIndex, org) {
            const orgId = parseInt(org.id || 0);
            const categoryId = parseInt(categ.id || 0);

            if (orgId <= 0 || categoryId <= 0) {
                return;
            }

            if (!Array.isArray(row.categs)) {
                this.$set(row, 'categs', []);
            }

            let selected = row.categs
                .map((id) => parseInt(id))
                .filter((id) => id > 0);

            if (categ.is_active === true) {
                if (this.isCategoryUsedInOtherSplit(categoryId, rowIndex, orgId)) {
                    categ.is_active = false;
                    this.showDuplicateCategory();
                    return;
                }

                if (!selected.includes(categoryId)) {
                    selected.push(categoryId);
                }
            } else {
                selected = selected.filter((id) => id !== categoryId);
            }

            this.$set(row, 'categs', selected);

            this.rebuildSplitCategories();
            this.emitChange();
        },

        clearOrgCategories(row) {
            this.$set(row, 'categs', []);

            this.rebuildSplitCategories();
            this.emitChange();
        },

        getOrgSelectedIds(row, org) {
            const orgId = parseInt(org?.id || 0);
            const activeOrgId = parseInt(row?.active_org_id || 0);

            if (orgId <= 0 || activeOrgId <= 0 || orgId !== activeOrgId) {
                return [];
            }

            if (!Array.isArray(row.categs)) {
                return [];
            }

            return row.categs
                .map((id) => parseInt(id))
                .filter((id) => id > 0);
        },

        getTotalSelectedForRow(row) {
            if (!Array.isArray(row.categs)) {
                return 0;
            }

            return row.categs
                .map((id) => parseInt(id))
                .filter((id) => id > 0)
                .length;
        },

        getOrgCategoriesForRow(row, org) {
            const orgId = parseInt(org.id || 0);

            if (orgId <= 0) {
                return [];
            }

            const source = this.categoriesByOrg[orgId] || [];
            const categories = this.clone(source);

            this.setCategoriesAttr(categories, row, org);

            return categories;
        },

        setCategoriesAttr(categories, row, org) {
            const selectedIds = this.getOrgSelectedIds(row, org);
            const orgId = parseInt(org.id || 0);

            categories.map((item) => {
                const id = parseInt(item.id || 0);

                item.is_active = selectedIds.includes(id);
                item.disabled = this.isCategoryUsedInOtherSplit(id, this.getRowIndex(row), orgId);

                if (item.hasOwnProperty('children') && Array.isArray(item.children)) {
                    this.setCategoriesAttr(item.children, row, org);
                }
            });
        },

        isCategoryUsedInOtherSplit(categoryId, currentRowIndex, orgId) {
            categoryId = parseInt(categoryId || 0);
            orgId = parseInt(orgId || 0);

            if (categoryId <= 0 || orgId <= 0) {
                return false;
            }

            let used = false;

            this.splitRows.forEach((row, rowIndex) => {
                if (rowIndex === currentRowIndex) {
                    return;
                }

                const rowOrgId = parseInt(row.active_org_id || 0);

                if (rowOrgId !== orgId) {
                    return;
                }

                const ids = Array.isArray(row.categs)
                    ? row.categs.map((id) => parseInt(id)).filter((id) => id > 0)
                    : [];

                if (ids.includes(categoryId)) {
                    used = true;
                }
            });

            return used;
        },

        getRowIndex(row) {
            return this.splitRows.findIndex((item) => {
                return item === row;
            });
        },

        rebuildSplitCategories() {
            let ids = [];

            this.splitRows.forEach((row) => {
                if (!Array.isArray(row.categs)) {
                    return;
                }

                row.categs.forEach((id) => {
                    id = parseInt(id);

                    if (id > 0 && !ids.includes(id)) {
                        ids.push(id);
                    }
                });
            });

            this.$set(this.payment.split, 'split_categories', ids);
        },

        hasSplitReceiverField() {
            const template = this.payment?.split?.split_template || [];

            if (Array.isArray(template)) {
                return template.some((row) => {
                    return row &&
                        typeof row === 'object' &&
                        Object.prototype.hasOwnProperty.call(row, 'splitReceiverId');
                });
            }

            return this.splitRows.some((row) => {
                return row &&
                    typeof row === 'object' &&
                    Object.prototype.hasOwnProperty.call(row, 'splitReceiverId');
            });
        },

        getTabTitle(row, rowIndex) {
            if (row.name) {
                return row.name;
            }

            if (row.okpo) {
                return row.okpo;
            }

            if (row.splitReceiverId) {
                const splitter = this.splitters.find((item) => item.id === row.splitReceiverId);

                if (splitter) {
                    return splitter.name;
                }

                return row.splitReceiverId;
            }

            return 'Split #' + (rowIndex + 1);
        },

        getSplitterOption(id) {
            if (!id) {
                return null;
            }

            return this.splitters.find((item) => item.id === id) || {
                id: id,
                name: id,
                okpo: '',
                edrpou: '',
                label: id
            };
        },

        setSplitterValue(row, fieldKey, option) {
            this.$set(row, fieldKey, option ? option.id : '');
            this.emitChange();
        },

        getOrgTitle(org) {
            if (!org) {
                return '';
            }

            return org.name || org.title || ('ORG #' + org.id);
        },

        showDuplicateCategory() {
            EventBus.$emit('show_toast', {
                err: __t('CATEG_ALREADY_SET'),
                type: 'warning',
                title: 'Payment Split'
            });
        },

        emitChange() {
            this.$emit('change', this.payment.split);
        },

        isCategoryLoading(orgId) {
            return this.loadingByOrg[parseInt(orgId || 0)] === true;
        },

        clone(value) {
            return JSON.parse(JSON.stringify(value || []));
        },

        makeKey() {
            return 'split_' + Date.now() + '_' + Math.floor(Math.random() * 100000);
        }
    },

    computed: {
        splitRows() {
            if (!this.payment.split || typeof this.payment.split !== 'object') {
                this.normalizeSplit();
            }

            if (!Array.isArray(this.payment.split.split_payment_data)) {
                this.$set(this.payment.split, 'split_payment_data', []);
            }

            return this.payment.split.split_payment_data;
        },

        drawOrganizations() {
            return this.organizations.filter((item) => {
                return parseInt(item.attached_products_clean_count || 0) > 0;
            });
        }
    },

    watch: {
        splitRows: {
            deep: true,
            handler() {
                this.rebuildSplitCategories();
                this.emitChange();
            }
        },

        payment: {
            deep: true,
            handler() {
                if (!this.payment.split || !Array.isArray(this.payment.split.split_payment_data)) {
                    this.normalizeSplit();
                }

                this.loadSplitters();
            }
        }
    }
}
</script>

<style scoped>
.split-card {
    border: 1px solid #ddd;
    border-radius: 6px;
    padding: 16px;
    margin-bottom: 15px;
    background: #fff;
}

.split-orgs {
    margin-top: 15px;
    background: #f8f8fb;
    border: 1px solid #e6e6ef;
    border-radius: 6px;
    padding: 14px;
}

.org-active-card {
    background: #fff;
    border: 1px solid #e1e1e8;
    border-radius: 6px;
    padding: 14px;
}

.org-active-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
}

.org-active-title {
    font-weight: 700;
    color: #333;
}

.org-active-meta {
    color: #7b7b8a;
    font-size: 12px;
}

.categories-scroll {
    max-height: 420px;
    overflow-y: auto;
    padding-right: 6px;
}

.gate-icon svg {
    max-width: 120px;
    max-height: 32px;
}
</style>
